import { booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, inject, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';

/**
 * ara-slide-toggle — dünner Ersatz für `mat-slide-toggle`. Funktion, API und Look sind bewusst
 * identisch zum Material-Standard (Drop-in: `[checked]`, `[disabled]`, `color`, `[hideIcon]`,
 * `(change)` mit {@link MatSlideToggleChange}, plus volle Reactive-/Template-Forms-Unterstützung als
 * ControlValueAccessor). Der EINZIGE Unterschied: der Handle bleibt in beiden Zuständen gleich groß —
 * MDC-Material-3 vergrößert ihn selektiert (16→24px), was in dichten Listen wie ein Höhensprung wirkt
 * (siehe SCSS). Zentraler Wrapper, damit weiteres Styling später an EINER Stelle passiert.
 */
@Component({
	selector: 'ara-slide-toggle',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatSlideToggleModule],
	templateUrl: './ara-slide-toggle.component.html',
	styleUrl: './ara-slide-toggle.component.scss',
	providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AraSlideToggleComponent), multi: true }],
})
export class AraSlideToggleComponent implements ControlValueAccessor {
	private readonly cdr = inject(ChangeDetectorRef);

	@Input({ transform: booleanAttribute }) checked = false;
	@Input({ transform: booleanAttribute }) disabled = false;
	@Input() color: ThemePalette;
	@Input() name: string | null = null;
	@Input({ transform: booleanAttribute }) hideIcon = false;
	@Input() labelPosition: 'before' | 'after' = 'after';
	@Input({ transform: booleanAttribute }) disableRipple = false;
	@Input({ transform: booleanAttribute }) required = false;
	@Input('aria-label') ariaLabel: string | null = null;

	// eslint-disable-next-line @angular-eslint/no-output-native -- spiegelt bewusst die Material-Slide-Toggle-API (change)
	@Output() readonly change = new EventEmitter<MatSlideToggleChange>();

	private onChange: (value: boolean) => void = () => {};
	protected onTouched: () => void = () => {};

	protected onInnerChange(event: MatSlideToggleChange): void {
		this.checked = event.checked;
		this.onChange(event.checked);
		this.onTouched();
		this.change.emit(event);
	}

	writeValue(value: boolean): void {
		this.checked = !!value;
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: boolean) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}
}
